/**
 * Codec for the original FWP replay text format (kept 1:1 so that legacy replays and the game's own
 * recorder stay compatible with the new backend session boundary).
 *
 * Format (docs/analysis/game-flow.md §10): `!<PARAMS>!<ROUNDS>!<checksum>!`
 *  - numbers: little-endian base 30, digit d -> char code d + 94 (`^`..`{`), negative prefix `*`
 *    (FWP.encode_replay_number / decode_replay_number, FWP.as:21253-21298);
 *  - ROUNDS: turns separated by `#`; a turn is `<tick>&<code>[%<repeat>|%<weapon>%<range>|%<x>%<y>]$...`
 *    (proc.append_replay_text, proc.as:3037-3054; FWP.decode_replay_move, FWP.as:21300-21356);
 *  - checksum: first char of encode_replay_number(sum of char codes of `!PARAMS!ROUNDS`) (FWP.as:16036-16045).
 */
import { type InputCommand, type InputFrame, cloneFrame, frameToLegacyCode, legacyCodeToFrame } from './input';

/** FWP.return_bytes_30 digits 0..2 (the encoder never emits more than three digits). */
function base30Digits(value: number): [number, number, number] {
  const d0 = value % 30;
  let rest = Math.round((value - d0) / 30);
  const d1 = rest % 30;
  rest = Math.round((rest - d1) / 30);
  const d2 = rest % 30;
  return [d0, d1, d2];
}

function digitChar(d: number): string {
  // String.fromCharCode applies ToUint16 (truncation) to fractional codes.
  return String.fromCharCode(Math.trunc(d + 94) & 0xffff);
}

/**
 * FWP.encode_replay_number. Values >= 27000 lose their high digits exactly like the original
 * (only three digits are written). Non-finite input is encoded as 0.
 * // PORT: AS3 would produce garbage for NaN; the port writes 0 instead.
 */
export function encodeReplayNumber(value: number): string {
  let n = Number.isFinite(value) ? value : 0;
  let out = '';
  if (n < 0) {
    out += '*';
    n = -n;
  }
  if (n < 30) return out + digitChar(n);
  const [d0, d1, d2] = base30Digits(n);
  if (n < 30 * 30) return out + digitChar(d0) + digitChar(d1);
  return out + digitChar(d0) + digitChar(d1) + digitChar(d2);
}

/**
 * FWP.decode_replay_number. `undefined` decodes to 0.
 * // PORT: AS3 throws a null reference error on a missing field; the port returns 0.
 */
export function decodeReplayNumber(text: string | undefined): number {
  if (text === undefined) return 0;
  let s = text;
  let sign = 1;
  if (s.charAt(0) === '*') {
    sign = -1;
    s = s.split('*')[1] ?? '';
  }
  let value = 0;
  for (let i = 0; i <= s.length - 1; i++) value += (s.charCodeAt(i) - 94) * Math.pow(30, i);
  return value * sign;
}

/** One entry of the decoded move table (`REPLAY_CUR_MOVE[tick]` in the original). */
export interface LegacyMoveEntry {
  code: string;
  repeats: number;
  weapon: number;
  range: number;
  clickX: number;
  clickY: number;
}

/**
 * FWP.decode_replay_move: parses one turn of the ROUNDS record into a tick-indexed table.
 * Later records at the same tick overwrite earlier ones, as in AS3.
 */
export function decodeLegacyTurn(turn: string): Map<number, LegacyMoveEntry> {
  const table = new Map<number, LegacyMoveEntry>();
  for (const rec of turn.split('$')) {
    if (rec.length === 0) continue;
    const [tickPart, body = ''] = rec.split('&');
    const tick = decodeReplayNumber(tickPart);
    const parts = body.split('%');
    const code = parts[0] ?? '';
    const entry: LegacyMoveEntry = { code, repeats: 0, weapon: 0, range: 0, clickX: 0, clickY: 0 };
    if (code === 'S') {
      entry.weapon = decodeReplayNumber(parts[1]);
      entry.range = decodeReplayNumber(parts[2]);
    }
    if (code === 'T') {
      entry.clickX = decodeReplayNumber(parts[1]);
      entry.clickY = decodeReplayNumber(parts[2]);
    }
    if (code !== 'S' && code !== 'T' && parts[1]) entry.repeats = decodeReplayNumber(parts[1]);
    table.set(tick, entry);
  }
  return table;
}

/** Converts a legacy move entry into a typed frame (weapon/click params are attached per frame kind). */
export function legacyEntryToFrame(entry: LegacyMoveEntry): InputFrame {
  const frame = legacyCodeToFrame(entry.code);
  if (frame.other === 'weapon') {
    frame.weapon = entry.weapon;
    frame.range = entry.range;
  } else if (frame.other === 'click') {
    frame.clickX = entry.clickX;
    frame.clickY = entry.clickY;
  }
  return frame;
}

/** proc.append_replay_text for one command. */
export function encodeLegacyCommand(cmd: InputCommand): string {
  const code = frameToLegacyCode(cmd.frame);
  let out = encodeReplayNumber(cmd.tick) + '&' + code;
  if (cmd.repeat !== 0) out += '%' + encodeReplayNumber(cmd.repeat);
  if (code === 'S') out += '%' + encodeReplayNumber(cmd.frame.weapon ?? 0) + '%' + encodeReplayNumber(cmd.frame.range ?? 0);
  if (code === 'T') out += '%' + encodeReplayNumber(cmd.frame.clickX ?? 0) + '%' + encodeReplayNumber(cmd.frame.clickY ?? 0);
  return out + '$';
}

/** Encodes the commands of one turn as a legacy ROUNDS segment (without the `#` separator). */
export function encodeLegacyTurn(commands: readonly InputCommand[]): string {
  let out = '';
  for (const cmd of commands) out += encodeLegacyCommand(cmd);
  return out;
}

/** Decodes a legacy ROUNDS segment into typed commands, ordered by tick. */
export function decodeLegacyTurnCommands(turn: string): InputCommand[] {
  const table = decodeLegacyTurn(turn);
  const ticks = [...table.keys()].sort((a, b) => a - b);
  return ticks.map((tick) => {
    const e = table.get(tick)!;
    return { tick, frame: legacyEntryToFrame(e), repeat: e.repeats };
  });
}

/**
 * Stateful reader with the exact semantics of `proc.read_key_press` (proc.as:3117-3143): a record with
 * `repeats > 0` is moved to the next tick with `repeats - 1` after being read (overwriting whatever was there).
 */
export class LegacyMovePlayer {
  private table: Map<number, LegacyMoveEntry>;

  constructor(turn: string | Map<number, LegacyMoveEntry>) {
    this.table = typeof turn === 'string' ? decodeLegacyTurn(turn) : new Map(turn);
  }

  read(tick: number): InputFrame & { weapon: number; range: number; clickX: number; clickY: number } {
    const out = { space: false, other: 'none' as InputFrame['other'], weapon: 0, range: 1, clickX: 0, clickY: 0 };
    const e = this.table.get(tick);
    if (e) {
      const f = legacyCodeToFrame(e.code);
      out.space = f.space;
      out.other = f.other;
      out.weapon = e.weapon;
      out.range = e.range;
      out.clickX = e.clickX;
      out.clickY = e.clickY;
      if (e.repeats !== 0) {
        this.table.set(tick + 1, { ...e, repeats: e.repeats - 1 });
        this.table.delete(tick);
      }
    }
    return out;
  }
}

/** Sum of char codes -> checksum char, as in FWP.end_game_fn / check_replay_string. */
export function legacyReplayChecksum(paramsRecord: string, roundsRecord: string): string {
  const body = '!' + paramsRecord + '!' + roundsRecord;
  let sum = 0;
  for (let i = 0; i < body.length; i++) sum += body.charCodeAt(i);
  return encodeReplayNumber(sum).charAt(0);
}

/** Builds `!PARAMS!ROUNDS!checksum!` exactly like FWP.end_game_fn (FWP.as:16037-16045). */
export function buildLegacyReplay(paramsRecord: string, roundsRecord: string): string {
  return '!' + paramsRecord + '!' + roundsRecord + '!' + legacyReplayChecksum(paramsRecord, roundsRecord) + '!';
}

export type LegacyReplayMode = 'career' | 'missions' | 'survival' | 'boss';

/** Parameters written by proc.append_replay_param (proc.as:3056-3115). */
export interface LegacyReplayParams {
  mode: LegacyReplayMode | null;
  playId: number;
  seed: number;
  attack: number;
  protect: number;
  health: number;
  spec: number;
  worms: number;
  tired: number;
  /** Perks as (PERKS_DEF id, level) pairs in recording order. */
  perks: { id: number; level: number }[];
  skill: number;
  impl: number;
}

export interface ParsedLegacyReplay {
  paramsRecord: string;
  roundsRecord: string;
  /** ROUNDS split into turns (`#`); index 0 is the first player turn. */
  turns: string[];
  checksumOk: boolean;
  params: LegacyReplayParams;
}

const MODE_BY_LETTER: Record<string, LegacyReplayMode> = { c: 'career', m: 'missions', s: 'survival', b: 'boss' };

/** Parses the PARAMS record. Sections are located by their letter prefixes like FWP.check_replay_string. */
export function parseLegacyParams(params: string): LegacyReplayParams {
  const out: LegacyReplayParams = {
    mode: null,
    playId: 0,
    seed: 0,
    attack: 0,
    protect: 0,
    health: 0,
    spec: 0,
    worms: 0,
    tired: 0,
    perks: [],
    skill: 0,
    impl: 0,
  };
  const prefix = params.includes('DE') ? 'DE' : 'DD';
  const modeLetter = params.split(prefix)[1]?.charAt(0) ?? '';
  out.mode = MODE_BY_LETTER[modeLetter] ?? null;
  // Records are `$`-terminated; each section starts with a letter tag.
  const recs = params.split('$');
  let i = 0;
  while (i < recs.length) {
    const rec = recs[i];
    const tag = rec.charAt(0);
    if (tag === 'I') out.playId = decodeReplayNumber(rec.slice(1));
    else if (tag === 'R') out.seed = decodeReplayNumber(rec.slice(1));
    else if (tag === 'P') {
      out.attack = decodeReplayNumber(rec.slice(1));
      out.protect = decodeReplayNumber(recs[i + 1]);
      out.health = decodeReplayNumber(recs[i + 2]);
      out.spec = decodeReplayNumber(recs[i + 3]);
      out.worms = decodeReplayNumber(recs[i + 4]);
      out.tired = decodeReplayNumber(recs[i + 5]);
      i += 6;
      // Perk list: the `S` tag is glued to the first perk record (or to `Z<skill>` when there are no
      // perks: `...$SZ<skill>$`), then `id%level` records follow until the `Z<skill>` record.
      let first = true;
      while (i < recs.length) {
        let r = recs[i];
        if (first) {
          if (r.charAt(0) !== 'S') break;
          r = r.slice(1);
          first = false;
        }
        i++;
        if (r.charAt(0) === 'Z') {
          out.skill = decodeReplayNumber(r.slice(1));
          break;
        }
        if (r.length > 0) {
          const [id, level] = r.split('%');
          out.perks.push({ id: decodeReplayNumber(id), level: decodeReplayNumber(level) });
        }
      }
      continue;
    } else if (tag === 'Z') out.skill = decodeReplayNumber(rec.slice(1));
    else if (tag === 'X') out.impl = decodeReplayNumber(rec.slice(1));
    i++;
  }
  return out;
}

/**
 * Inverse of parseLegacyParams, in the order proc.init_gameplay writes the records
 * (play_type proc.as:482, play_id :486, random :502, player :543).
 */
export function encodeLegacyParams(p: LegacyReplayParams): string {
  const n = encodeReplayNumber;
  let s = '';
  if (p.mode) s += 'DE' + p.mode.charAt(0) + '$';
  s += 'I' + n(p.playId) + '$';
  s += 'R' + n(p.seed) + '$';
  s += 'P' + n(p.attack) + '$' + n(p.protect) + '$' + n(p.health) + '$' + n(p.spec) + '$' + n(p.worms) + '$' + n(p.tired) + '$';
  s += 'S';
  for (const perk of p.perks) s += n(perk.id) + '%' + n(perk.level) + '$';
  s += 'Z' + n(p.skill) + '$';
  s += 'X' + n(p.impl) + '$';
  return s;
}

/** Parses a full legacy replay string. Throws on a structurally invalid string. */
export function parseLegacyReplay(replay: string): ParsedLegacyReplay {
  const parts = replay.split('!');
  if (parts.length < 4) throw new Error('Legacy replay must have the form !PARAMS!ROUNDS!checksum!');
  const paramsRecord = parts[1];
  const roundsRecord = parts[2];
  const checksum = (parts[3] ?? '').charAt(0);
  return {
    paramsRecord,
    roundsRecord,
    turns: roundsRecord.split('#'),
    checksumOk: checksum === legacyReplayChecksum(paramsRecord, roundsRecord),
    params: parseLegacyParams(paramsRecord),
  };
}

/** Expands commands into a tick -> frame map (for tests and headless validators). */
export function expandCommands(commands: readonly InputCommand[]): Map<number, InputFrame> {
  const out = new Map<number, InputFrame>();
  for (const c of commands) {
    for (let k = 0; k <= c.repeat; k++) out.set(c.tick + k, cloneFrame(c.frame));
  }
  return out;
}
